import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'

import { beginIdentityChange, endIdentityChange } from '@core/app/slices/auth'
import {
    AuthApi,
    AuthData,
    AuthResponse,
    FirstRegistrationStepVerify,
    Login,
    LoginResponse,
    PhonePrefixes,
    SecondRegistrationStepVerify,
    StepVerification,
    ThirdRegistrationStepVerify,
} from '@core/app/types/authApiType'

import { applyJobV2Api } from './applyJobV2Api'
import { postsApi } from './postsApi'
import { profileApi } from './profileApi'

async function refreshIdentityCaches(
    _: unknown,
    {
        dispatch,
        queryFulfilled,
    }: {
        dispatch: (
            action: ReturnType<
                typeof profileApi.util.resetApiState | typeof beginIdentityChange | typeof endIdentityChange
            >
        ) => unknown
        queryFulfilled: Promise<{ data: unknown }>
    }
) {
    dispatch(beginIdentityChange())
    try {
        const { data } = await queryFulfilled
        if (
            data &&
            typeof data === 'object' &&
            (('isMFA' in data && data.isMFA) || ('success' in data && data.success === false))
        )
            return
        // These responses belong to the previous authenticated session.
        dispatch(postsApi.util.resetApiState())
        dispatch(applyJobV2Api.util.resetApiState())
        dispatch(profileApi.util.resetApiState())
    } catch {
        // Failed authentication must preserve the current session and its draft.
    } finally {
        dispatch(endIdentityChange())
    }
}

// Same cache reset as `refreshIdentityCaches`, for a login that already succeeded in place.
export function refreshIdentityAfterLogin(dispatch: (action: never) => unknown) {
    dispatch(beginIdentityChange() as never)
    try {
        dispatch(postsApi.util.resetApiState() as never)
        dispatch(applyJobV2Api.util.resetApiState() as never)
        dispatch(profileApi.util.resetApiState() as never)
    } finally {
        dispatch(endIdentityChange() as never)
    }
}

export const authApi = createApi({
    reducerPath: 'authApi',
    baseQuery: fetchBaseQuery({
        baseUrl: process.env.ENV === 'local' ? `${process.env.API_URL}` : `${window.location.origin}`,
        prepareHeaders: (headers) => {
            const token = document.getElementsByName('csrf-token')[0]?.getAttribute('content')
            if (token) headers.set('X-CSRF-TOKEN', token)

            const customCorporation = document.getElementById('__custom_route_data')?.getAttribute('data-corpId')
            if (customCorporation) headers.set('X-CORP-CONTEXT', customCorporation)

            return headers
        },
        credentials: process.env.ENV === 'local' ? 'include' : 'same-origin',
    }),
    endpoints: (builder) => ({
        getRegisterQuery: builder.query<AuthApi, { code?: string }>({
            query: ({ code }) => ({
                url: `/api/register/settings${code && code.length > 0 ? code : ''}`,
                method: 'GET',
            }),
        }),
        postRegisterData: builder.mutation<AuthResponse, AuthData>({
            query: (body) => ({
                url: '/api/register',
                method: 'POST',
                body,
            }),
        }),
        getPhonePrefixes: builder.query<PhonePrefixes, void>({
            query: () => ({
                url: '/api/phone-prefix',
                method: 'GET',
            }),
        }),
        getFirstStepVerify: builder.mutation<StepVerification, FirstRegistrationStepVerify>({
            query: (body) => ({
                url: '/api/register/step/1',
                method: 'POST',
                body,
            }),
        }),
        getSecondStepVerify: builder.mutation<StepVerification, SecondRegistrationStepVerify>({
            query: (body) => ({
                url: '/api/register/step/2',
                method: 'POST',
                body,
            }),
        }),
        getThirdStepVerify: builder.mutation<StepVerification, ThirdRegistrationStepVerify>({
            query: (body) => ({
                url: '/api/register/step/3',
                method: 'POST',
                body,
            }),
        }),
        login: builder.mutation<LoginResponse, Login>({
            onQueryStarted: refreshIdentityCaches,
            query: (body) => ({
                url: '/api/login',
                method: 'POST',
                body,
            }),
        }),
        logout: builder.mutation<{ redirectUrl: string; statusCode: number; success: boolean }, void>({
            onQueryStarted: refreshIdentityCaches,
            query: () => ({
                url: '/api/logout',
                method: 'POST',
            }),
        }),
        getEmailVerificationCode: builder.mutation<
            void,
            { data: { email: string; 'g-recaptcha-response': string } }
        >({
            query: (body) => ({
                url: '/api/email-validator/send',
                method: 'POST',
                body,
            }),
        }),
        sendEmailVerificationCode: builder.mutation<{ data: { id: number } }, { data: { code: string } }>({
            query: (body) => ({
                url: '/api/email-validator/validate',
                method: 'POST',
                body,
            }),
        }),
        sendSms: builder.mutation<
            void,
            { data: { ['g-recaptcha-response']: string; mobilePhone: { prefix: string; number: string } } }
        >({
            query: (body) => ({
                url: '/api/phone-validator/send-sms',
                method: 'POST',
                body,
            }),
        }),
        sendMobileVerificationCode: builder.mutation<{ data: { id: number } }, { data: { code: string } }>({
            query: (body) => ({
                url: '/api/phone-validator/validate',
                method: 'POST',
                body,
            }),
        }),
        getExternalCode: builder.mutation<
            { link: string; statusCode: number },
            { data: { serviceId: number; typeId: number; jobPostId?: number } }
        >({
            query: (body) => ({
                url: '/oauth/request/link',
                method: 'POST',
                body,
            }),
        }),
        loginViaSMS: builder.mutation<LoginResponse, { data: { code: string; ['g-recaptcha-response']: string } }>({
            onQueryStarted: refreshIdentityCaches,
            query: (body) => ({
                url: '/api/login/sms',
                method: 'POST',
                body,
            }),
        }),
        // Apply form one-time-code login. `Response-V2` makes the backend use the structured error format.
        sendApplyLoginEmail: builder.mutation<
            { success?: boolean; data?: { result?: boolean } },
            { email: string; 'g-recaptcha-response': string }
        >({
            query: (body) => ({
                url: '/api/login/send-email',
                method: 'POST',
                headers: { 'Response-V2': '1' },
                body,
            }),
        }),
        sendApplyLoginSms: builder.mutation<
            { success?: boolean; data?: { result?: boolean } },
            { mobilePhone: { prefix: string; number: string }; 'g-recaptcha-response': string }
        >({
            query: (body) => ({
                url: '/api/login/send-sms',
                method: 'POST',
                headers: { 'Response-V2': '1' },
                body,
            }),
        }),
        // No identity refresh on start: a wrong code must not remount the Apply form.
        // Call `refreshIdentityAfterLogin` once the code was accepted.
        validateApplyLoginCode: builder.mutation<{ success?: boolean; data?: { success?: boolean } }, { code: string }>(
            {
                query: (body) => ({
                    url: '/api/login/validate',
                    method: 'POST',
                    headers: { 'Response-V2': '1' },
                    body,
                }),
            }
        ),
        loginViaService: builder.mutation<LoginResponse, { code: string }>({
            onQueryStarted: refreshIdentityCaches,
            query: ({ code }) => ({
                url: `/api/login/external${code?.length > 0 ? code : ''}`,
                method: 'GET',
            }),
        }),
        initResetPassword: builder.mutation<void, { data: { email: string; 'g-recaptcha-response': string } }>({
            query: (body) => ({
                url: '/api/reset-password/init',
                method: 'POST',
                body,
            }),
        }),
        validateResetPassword: builder.mutation<void, { data: { email: string; code: string } }>({
            query: (body) => ({
                url: '/api/reset-password/validate',
                method: 'POST',
                body,
            }),
        }),
        resetForgotPassword: builder.mutation<
            void,
            { data: { email: string; code: string; password: string; 'g-recaptcha-response': string } }
        >({
            query: (body) => ({
                url: '/api/reset-password/reset',
                method: 'POST',
                body,
            }),
        }),
        acceptCookies: builder.mutation<void, { statistic: boolean; marketing: boolean }>({
            query: (body) => ({
                url: '/api/cookie',
                method: 'POST',
                body,
            }),
        }),
    }),
})

export const {
    useGetRegisterQueryQuery,
    usePostRegisterDataMutation,
    useGetPhonePrefixesQuery,
    useGetFirstStepVerifyMutation,
    useGetSecondStepVerifyMutation,
    useGetThirdStepVerifyMutation,
    useLoginMutation,
    useLogoutMutation,
    useGetEmailVerificationCodeMutation,
    useSendEmailVerificationCodeMutation,
    useSendSmsMutation,
    useSendMobileVerificationCodeMutation,
    useGetExternalCodeMutation,
    useLoginViaSMSMutation,
    useSendApplyLoginEmailMutation,
    useSendApplyLoginSmsMutation,
    useValidateApplyLoginCodeMutation,
    useLoginViaServiceMutation,
    useInitResetPasswordMutation,
    useValidateResetPasswordMutation,
    useResetForgotPasswordMutation,
    useAcceptCookiesMutation,
} = authApi
